import React from "react";
import { Link } from "react-router-dom";
import { Youtube, Facebook, Instagram } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useIsAndroidApp } from "@/hooks/useLaunchContext";
import { GENERATORS } from "@/data/generators";
import { SOCIAL_PROFILES } from "@/lib/seoSchemas";

const SOCIAL_ICONS = {
  youtube: Youtube,
  facebook: Facebook,
  instagram: Instagram,
  tiktok: (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  ),
};

export default function Footer() {
  const { t, language } = useI18n();
  const isEn = language === "en";
  // Language-aware generator links: only list pages that actually exist for the current
  // language, to avoid ever linking to a non-existent EN page (some genres are FR-only).
  const generatorLinks = Object.entries(GENERATORS)
    .map(([slug, entry]) => {
      const path = isEn ? entry.pathEn : entry.pathFr;
      if (!path) return null;
      const content = entry[isEn ? "en" : "fr"] || entry.fr;
      return { slug, path, label: content.h1.split(":")[0].split("|")[0].trim() };
    })
    .filter(Boolean);
  // Google Play review expects an app-like experience inside the TWA, not "a website with a
  // link list" - the marketing/SEO footer (with dozens of internal links) is only useful for
  // the regular web version (SEO), so it's entirely hidden inside the Android app.
  const isAndroidApp = useIsAndroidApp();
  if (isAndroidApp) return null;
  return (
    <footer className="border-t border-border bg-card/50 py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-6">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Kalypso Music" className="h-8 w-8 rounded-lg object-cover" />
              <span className="font-heading text-base font-semibold">Kalypso Music</span>
            </div>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">{t("landing.footerTagline")}</p>
            <div className="mt-4 flex items-center gap-3" data-testid="footer-social-links">
              {SOCIAL_PROFILES.map((p) => {
                const Icon = SOCIAL_ICONS[p.id];
                return (
                  <a
                    key={p.id}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Kalypso Music sur ${p.label}`}
                    data-testid={`footer-social-${p.id}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
            <div className="mt-5">
              <h4 className="font-heading text-sm font-semibold">{isEn ? "Popular guides" : "Guides populaires"}</h4>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li><Link to="/blog/chanson-personnalisee-mariage" className="hover:text-foreground">{isEn ? "Personalized wedding song" : "Chanson personnalisée mariage"}</Link></li>
                <li><Link to="/blog/chanson-anniversaire-personnalisee" className="hover:text-foreground">{isEn ? "Birthday song with name" : "Chanson d'anniversaire avec prénom"}</Link></li>
                <li><Link to="/blog/guide-cadeau-musical-personnalise" className="hover:text-foreground">{isEn ? "Musical gift guide" : "Guide du cadeau musical"}</Link></li>
                <li><Link to="/blog/meilleurs-generateurs-musique-ia-2026" className="hover:text-foreground">{isEn ? "Best AI music generators" : "Meilleurs générateurs musique IA"}</Link></li>
              </ul>
            </div>
          </div>
          <div>
            <h4 className="font-heading text-sm font-semibold">{t("nav.pricing")}</h4>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li><Link to="/#pricing" className="hover:text-foreground">{t("nav.pricing")}</Link></li>
              <li><Link to="/services" className="hover:text-foreground">Services</Link></li>
              <li><Link to="/creer-chanson-personnalisee" className="hover:text-foreground">Chanson personnalisée</Link></li>
              <li><Link to="/cadeau-musical-personnalise" className="hover:text-foreground">Cadeau musical</Link></li>
              <li><Link to="/votre-histoire-en-musique" className="hover:text-foreground">Votre histoire en musique</Link></li>
              <li><Link to="/create" className="hover:text-foreground">{t("nav.create")}</Link></li>
              <li><Link to="/faq" className="hover:text-foreground">{t("nav.faq")}</Link></li>
              <li><Link to="/avis" className="hover:text-foreground">{t("nav.reviews")}</Link></li>
              <li><Link to="/galerie" className="hover:text-foreground">{t("nav.gallery")}</Link></li>
              <li><Link to="/blog" className="hover:text-foreground">Blog</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-sm font-semibold">{isEn ? "AI Music Generators" : "Générateurs IA Musicale"}</h4>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              {generatorLinks.map((g) => (
                <li key={g.slug}><Link to={g.path} className="hover:text-foreground">{g.label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-sm font-semibold">{t("landing.footerOccasions")}</h4>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li><Link to="/musique-mariage" className="hover:text-foreground">Chanson de mariage</Link></li>
              <li><Link to="/musique-anniversaire" className="hover:text-foreground">Chanson d'anniversaire</Link></li>
              <li><Link to="/musique-naissance" className="hover:text-foreground">Chanson de naissance</Link></li>
              <li><Link to="/idee-cadeau-original" className="hover:text-foreground">Idée cadeau originale</Link></li>
              <li><Link to="/chanson-enterrement-hommage" className="hover:text-foreground">Chanson d'hommage</Link></li>
              <li><Link to="/chanson-depart-retraite" className="hover:text-foreground">Départ à la retraite</Link></li>
              <li><Link to="/chanson-fiancailles" className="hover:text-foreground">Chanson de fiançailles</Link></li>
              <li><Link to="/chanson-evjf-evg" className="hover:text-foreground">Chanson EVJF / EVG</Link></li>
              <li><Link to="/chanson-remise-diplome" className="hover:text-foreground">Remise de diplôme</Link></li>
              <li><Link to="/chanson-baby-shower" className="hover:text-foreground">Baby shower</Link></li>
              <li><Link to="/chanson-fete-des-meres" className="hover:text-foreground">Fête des mères</Link></li>
              <li><Link to="/chanson-fete-des-peres" className="hover:text-foreground">Fête des pères</Link></li>
              <li><Link to="/chanson-saint-valentin" className="hover:text-foreground">Saint-Valentin</Link></li>
              <li><Link to="/chanson-cadeau-noel" className="hover:text-foreground">Cadeau de Noël</Link></li>
              <li><Link to="/chanson-anniversaire-mariage" className="hover:text-foreground">Anniversaire de mariage</Link></li>
              <li><Link to="/chanson-pot-de-depart-collegue" className="hover:text-foreground">Pot de départ collègue</Link></li>
              <li><Link to="/chanson-remerciement" className="hover:text-foreground">Chanson de remerciement</Link></li>
              <li><Link to="/chanson-bapteme" className="hover:text-foreground">Chanson de baptême</Link></li>
              <li><Link to="/chanson-cadeau-amitie" className="hover:text-foreground">Cadeau d'amitié</Link></li>
              <li><Link to="/message-vocal-personnalise" className="hover:text-foreground">Message vocal personnalisé</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-sm font-semibold">{t("landing.footerLegal")}</h4>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              <li><Link to="/mentions-legales" className="hover:text-foreground">{t("landing.footerLegal")}</Link></li>
              <li><Link to="/confidentialite" className="hover:text-foreground">{t("legalDocs.privacyNav")}</Link></li>
              <li><Link to="/cgu" className="hover:text-foreground">{t("legalDocs.termsNav")}</Link></li>
              <li><Link to="/cgv" className="hover:text-foreground">{t("legalDocs.salesNav")}</Link></li>
              <li><Link to="/cookies" className="hover:text-foreground">{t("legalDocs.cookiesNav")}</Link></li>
              <li><Link to="/refunds" className="hover:text-foreground">{t("landing.footerRefunds")}</Link></li>
              <li><Link to="/contact" className="hover:text-foreground">{t("legalDocs.contactNav")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 border-t border-border pt-6 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Kalypso Music. {t("landing.footerRights")}
        </div>
      </div>
    </footer>
  );
}

